// Browser equivalent of the mobile apps' deviceInfo helper. Produces the same
// ClientDeviceInfo shape the backend's sanitizeDeviceInfo whitelist expects, so
// a web login seeds productMetadata.SUPER_PREP.lastDevice + installedPlatforms
// (platform 'WEB'). All fields are best-effort; never throws.
export interface ClientDeviceInfo {
  platform: 'ANDROID' | 'IOS' | 'WEB';
  osName?: string;
  osVersion?: string;
  model?: string;
  brand?: string;
  appVersion?: string;
  locale?: string;
}

function bestEffortLocale(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().locale;
  } catch {
    return undefined;
  }
}

// Coarse OS sniff from the UA string — enough for support triage / analytics
// without pulling in a heavy UA-parsing dependency.
function detectOs(ua: string): { osName?: string; osVersion?: string } {
  let m: RegExpMatchArray | null;
  if ((m = ua.match(/Windows NT ([0-9.]+)/))) return { osName: 'Windows', osVersion: m[1] };
  if ((m = ua.match(/Android ([0-9.]+)/))) return { osName: 'Android', osVersion: m[1] };
  if ((m = ua.match(/(?:iPhone|iPad).*?OS ([0-9_]+)/))) return { osName: 'iOS', osVersion: m[1].replace(/_/g, '.') };
  if ((m = ua.match(/Mac OS X ([0-9_]+)/))) return { osName: 'macOS', osVersion: m[1].replace(/_/g, '.') };
  if (/Linux/.test(ua)) return { osName: 'Linux' };
  return {};
}

export function getDeviceInfo(): ClientDeviceInfo | null {
  if (typeof navigator === 'undefined') return null;

  const info: ClientDeviceInfo = { platform: 'WEB' };

  const ua = navigator.userAgent || '';
  const { osName, osVersion } = detectOs(ua);
  if (osName) info.osName = osName;
  if (osVersion) info.osVersion = osVersion;

  const locale = bestEffortLocale();
  if (locale) info.locale = locale;

  return info;
}
